import React, { useMemo, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  ChartType,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  TooltipPositionerFunction,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../common/usePageTitle';
import { useConversion, useMe, useSecondPillarAssets } from '../common/apiHooks';
import { SecondPillarAssets } from '../common/apiModels';
import { Shimmer } from '../common/shimmer/Shimmer';
import { formatAmountForCurrency } from '../common/utils';
import { TranslationKey } from '../translations';

declare module 'chart.js' {
  interface TooltipPositionerMap {
    segmentCenter: TooltipPositionerFunction<ChartType>;
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

Tooltip.positioners.segmentCenter = function segmentCenter(elements) {
  if (!elements.length) {
    return false;
  }
  const element = elements[0].element as BarElement;
  return element.getCenterPoint();
};

const STACKED_BAR_SEPARATOR_WIDTH = 1;
const STACKED_BAR_SEPARATOR_COLOR = '#fff';
const STACK_KEY = 'balance';
const SWATCH_SIZE = 14;

// Blue = employeeWithheldPortion * 0.80 + additionalParentalBenefit.
// Yellow = employeeWithheldPortion * 0.20 + socialTaxPortion + compensation
// + interest + corrections. The 80/20 split uses Estonia's historically
// dominant 20% income-tax rate — approximate values are prefixed with "~"
// in the tooltip.
const INCOME_TAX_RATE = 0.2;

type TooltipEntry = { labelId: TranslationKey; amount: number; approximate?: boolean };

const contributionBreakdown = (assets: SecondPillarAssets): TooltipEntry[] => {
  const entries: TooltipEntry[] = [];
  if (assets.employeeWithheldPortion > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.netContribution',
      amount: assets.employeeWithheldPortion * (1 - INCOME_TAX_RATE),
      approximate: true,
    });
  }
  if (assets.additionalParentalBenefit > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.parentalBenefit',
      amount: assets.additionalParentalBenefit,
    });
  }
  if (assets.interest > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.interest',
      amount: assets.interest,
    });
  }
  if (assets.corrections > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.corrections',
      amount: assets.corrections,
    });
  }
  return entries;
};

const taxWinBreakdown = (assets: SecondPillarAssets): TooltipEntry[] => {
  const entries: TooltipEntry[] = [];
  const socialTaxAmount = assets.socialTaxPortion + assets.compensation;
  if (socialTaxAmount > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.socialTax',
      amount: socialTaxAmount,
    });
  }
  if (assets.employeeWithheldPortion > 0) {
    entries.push({
      labelId: 'secondPillarGrowth.tooltip.taxGain',
      amount: assets.employeeWithheldPortion * INCOME_TAX_RATE,
      approximate: true,
    });
  }
  return entries;
};

const formatBreakdown = (
  entries: TooltipEntry[],
  intl: { formatMessage: (descriptor: { id: TranslationKey }) => string },
  formatCurrency: (value: number) => string,
): string[] =>
  entries.map(
    ({ labelId, amount, approximate }) =>
      `${intl.formatMessage({ id: labelId })}: ${approximate ? '~' : ''}${formatCurrency(amount)}`,
  );

// Chart palette mirrors Tuleva's brand illustrations ("Laura" charts):
// bright blue for the member's contribution, warm yellow for the
// social-tax share, saturated green for return, warm orange for the
// rare inheritance, muted red for a loss, and neutral grey below the
// axis for withdrawn money.
const COLOR_CONTRIBUTION = '#00AEEA'; // Tuleva Bright Blue
const COLOR_STATE = '#FDD835'; // warm yellow
const COLOR_INHERITANCE = '#F29B0F'; // warm orange
const COLOR_GROWTH_POSITIVE = '#52A560'; // saturated green
const COLOR_GROWTH_NEGATIVE = '#D55C5C'; // muted brand-adjacent red
const COLOR_WITHDRAWN = '#9AA4AE'; // neutral grey
const COLOR_TRANSFERRED_TO_PIK = '#BDC5CD'; // slightly lighter grey for PIK outflows

const HOVER_CONTRIBUTION = '#0098CC';
const HOVER_STATE = '#E8C41E';
const HOVER_INHERITANCE = '#D48800';
const HOVER_GROWTH_POSITIVE = '#418C50';
const HOVER_GROWTH_NEGATIVE = '#BE4848';
const HOVER_WITHDRAWN = '#7F8A94';
const HOVER_TRANSFERRED_TO_PIK = '#A3ACB4';

type AccountRow = {
  labelId: TranslationKey;
  amount: number;
  color: string;
};

type Segments = {
  contribution: number;
  state: number;
  inheritance: number;
  growth: number;
  withdrawn: number;
  transferredToPik: number;
};

const orderedAccountRows = (segments: Segments): AccountRow[] => {
  const netWithdrawn = -segments.withdrawn;
  const growthColor = segments.growth >= 0 ? COLOR_GROWTH_POSITIVE : COLOR_GROWTH_NEGATIVE;
  const rows: AccountRow[] = [];
  // Positive segments, top-of-bar to just-above-zero (reverse of dataset order)
  if (segments.growth >= 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.growth',
      amount: segments.growth,
      color: growthColor,
    });
  }
  if (segments.inheritance > 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.inheritance',
      amount: segments.inheritance,
      color: COLOR_INHERITANCE,
    });
  }
  if (segments.state > 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.government',
      amount: segments.state,
      color: COLOR_STATE,
    });
  }
  if (segments.contribution > 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.own',
      amount: segments.contribution,
      color: COLOR_CONTRIBUTION,
    });
  }
  // Negative segments, closest-to-zero first
  if (segments.growth < 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.growth',
      amount: segments.growth,
      color: growthColor,
    });
  }
  if (segments.transferredToPik > 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.transferredToPik',
      amount: -segments.transferredToPik,
      color: COLOR_TRANSFERRED_TO_PIK,
    });
  }
  if (netWithdrawn > 0) {
    rows.push({
      labelId: 'secondPillarGrowth.account.withdrawals',
      amount: segments.withdrawn,
      color: COLOR_WITHDRAWN,
    });
  }
  return rows;
};

type CtaItem = {
  id: 'taxWin' | 'indexFund' | 'raiseRate';
  done: boolean;
  link: string | null;
};

const LOW_FEE_THRESHOLD = 0.005;

const SecondPillarGrowth = () => {
  const intl = useIntl();
  usePageTitle('pageTitle.secondPillarGrowth');
  const { data: assets } = useSecondPillarAssets();
  const { data: user } = useMe();
  const { data: conversion } = useConversion();
  const currentPaymentRate = user?.secondPillarPaymentRates?.current ?? 2;
  const pendingPaymentRate = user?.secondPillarPaymentRates?.pending ?? currentPaymentRate;

  const ctaItems: CtaItem[] = useMemo(() => {
    const secondPillarConversion = conversion?.secondPillar;
    return [
      {
        id: 'taxWin',
        done: user?.secondPillarActive ?? false,
        link: null,
      },
      {
        id: 'indexFund',
        done: Boolean(
          secondPillarConversion && secondPillarConversion.weightedAverageFee <= LOW_FEE_THRESHOLD,
        ),
        link: '/2nd-pillar-flow',
      },
      {
        id: 'raiseRate',
        done: pendingPaymentRate >= 6,
        link: '/2nd-pillar-payment-rate',
      },
    ];
  }, [user, conversion, currentPaymentRate, pendingPaymentRate]);
  const ctaAllDone = ctaItems.every((item) => item.done);
  const contributionLabel = intl.formatMessage({
    id: 'secondPillarGrowth.chart.yourContributions',
  });
  const taxAdvantageLabel = intl.formatMessage({
    id: 'secondPillarGrowth.chart.government',
  });

  const segments: Segments | null = useMemo(() => {
    if (!assets) {
      return null;
    }
    const contribution =
      assets.employeeWithheldPortion * (1 - INCOME_TAX_RATE) +
      assets.additionalParentalBenefit +
      assets.interest +
      assets.corrections;
    const state =
      assets.employeeWithheldPortion * INCOME_TAX_RATE +
      assets.socialTaxPortion +
      assets.compensation;
    const inheritance = assets.inheritance + assets.insurance;
    // transferredToPik is missing when the frontend runs against an older
    // backend that doesn't compute it yet; fall back to 0 so we degrade to
    // the pre-PIK-aware formula instead of rendering NaN.
    const transferredToPik = assets.transferredToPik ?? 0;
    const growth =
      assets.balance + assets.withdrawals + transferredToPik - contribution - state - inheritance;
    const withdrawn = -assets.withdrawals;
    return { contribution, state, inheritance, growth, withdrawn, transferredToPik };
  }, [assets]);

  // Sum of the chart's above-zero segments — equals balance for users with
  // non-negative growth and no PIK transfer, but stays meaningful when the
  // current balance is dragged to zero by negative growth, withdrawals, or
  // PIK outflows.
  const positiveStackTotal = segments
    ? segments.contribution + segments.state + segments.inheritance + Math.max(segments.growth, 0)
    : 0;

  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const subNoteKey = useMemo(() => {
    if (!segments || !assets || assets.pikFlag) {
      return null;
    }
    if (segments.growth < 0) {
      return 'negativeReturn';
    }
    if (
      assets.employeeWithheldPortion === 0 &&
      (assets.additionalParentalBenefit > 0 || assets.socialTaxPortion > 0)
    ) {
      return 'parentalLeave';
    }
    return null;
  }, [segments, assets]);

  const renderArrow = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-arrow-right"
      viewBox="0 0 16 16"
    >
      <path
        fillRule="evenodd"
        d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"
      />
    </svg>
  );

  const renderCheckIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="currentColor"
      viewBox="0 0 16 16"
      className="flex-shrink-0 text-success"
      style={{ marginTop: '0.1em' }}
      aria-hidden
    >
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.011-1.05z" />
    </svg>
  );

  const renderCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      viewBox="0 0 16 16"
      className="flex-shrink-0 text-secondary"
      style={{ marginTop: '0.1em' }}
      aria-hidden
    >
      <circle cx="8" cy="8" r="7" />
    </svg>
  );

  const renderCtaItemLabel = (item: CtaItem, labelId: TranslationKey) => {
    if (item.done) {
      return (
        <span className="text-secondary">
          <FormattedMessage id={labelId} />
        </span>
      );
    }
    if (item.link) {
      return (
        <Link to={item.link}>
          <FormattedMessage id={labelId} />
        </Link>
      );
    }
    return (
      <span>
        <FormattedMessage id={labelId} />
      </span>
    );
  };

  const ctaContent = (
    <>
      <h2 className="m-0 h3">
        <FormattedMessage
          id={ctaAllDone ? 'secondPillarGrowth.cta.titleAllDone' : 'secondPillarGrowth.cta.title'}
        />
      </h2>
      <ul className="list-unstyled m-0 d-flex flex-column gap-2">
        {ctaItems.map((item) => {
          const labelId = `secondPillarGrowth.cta.item.${item.id}` as TranslationKey;
          return (
            <li
              key={item.id}
              className="d-flex align-items-start gap-2"
              data-testid={`cta-item-${item.id}`}
              data-done={item.done ? 'true' : 'false'}
            >
              {item.done ? renderCheckIcon() : renderCircleIcon()}
              {renderCtaItemLabel(item, labelId)}
            </li>
          );
        })}
      </ul>
    </>
  );

  const thirdPillarUpsell = ctaAllDone && (
    <>
      <h2 className="m-0 h3">
        <FormattedMessage id="secondPillarGrowth.cta.wantToSaveMore" />
      </h2>
      <p className="m-0">
        <Link to="/3rd-pillar-payment" className="icon-link icon-link-hover fw-medium lead">
          <FormattedMessage id="secondPillarGrowth.cta.makeThirdPillarContribution" />
          {renderArrow()}
        </Link>
      </p>
      <p className="m-0">
        <FormattedMessage id="secondPillarGrowth.cta.thirdPillarAdditional" />
      </p>
    </>
  );

  const formatCurrency = (value: number) => formatAmountForCurrency(value, 0);

  const chartData = (() => {
    if (!segments) {
      return null;
    }

    const growthIsPositive = segments.growth >= 0;
    const rawDatasets = [
      {
        label: contributionLabel,
        value: segments.contribution,
        color: COLOR_CONTRIBUTION,
        hover: HOVER_CONTRIBUTION,
      },
      {
        label: intl.formatMessage({ id: 'secondPillarGrowth.chart.government' }),
        value: segments.state,
        color: COLOR_STATE,
        hover: HOVER_STATE,
      },
      ...(segments.inheritance > 0
        ? [
            {
              label: intl.formatMessage({ id: 'secondPillarGrowth.chart.inheritance' }),
              value: segments.inheritance,
              color: COLOR_INHERITANCE,
              hover: HOVER_INHERITANCE,
            },
          ]
        : []),
      {
        label: intl.formatMessage({ id: 'secondPillarGrowth.chart.growth' }),
        value: segments.growth,
        color: growthIsPositive ? COLOR_GROWTH_POSITIVE : COLOR_GROWTH_NEGATIVE,
        hover: growthIsPositive ? HOVER_GROWTH_POSITIVE : HOVER_GROWTH_NEGATIVE,
      },
      {
        label: intl.formatMessage({ id: 'secondPillarGrowth.chart.withdrawals' }),
        value: segments.withdrawn,
        color: COLOR_WITHDRAWN,
        hover: HOVER_WITHDRAWN,
      },
      {
        label: intl.formatMessage({ id: 'secondPillarGrowth.chart.transferredToPik' }),
        value: segments.transferredToPik > 0 ? -segments.transferredToPik : 0,
        color: COLOR_TRANSFERRED_TO_PIK,
        hover: HOVER_TRANSFERRED_TO_PIK,
      },
    ];

    // Top of visual stack: last dataset with a positive value.
    // Bottom of visual stack: last dataset with a negative value.
    let topPositiveIdx = -1;
    let bottomNegativeIdx = -1;
    rawDatasets.forEach((ds, i) => {
      if (ds.value > 0) {
        topPositiveIdx = i;
      }
      if (ds.value < 0) {
        bottomNegativeIdx = i;
      }
    });

    const roundedCorner = 6;

    return {
      labels: [intl.formatMessage({ id: 'secondPillarGrowth.chart.label' })],
      datasets: rawDatasets.map((ds, i) => ({
        label: ds.label,
        data: [ds.value],
        backgroundColor: ds.color,
        hoverBackgroundColor: ds.hover,
        borderColor: STACKED_BAR_SEPARATOR_COLOR,
        borderWidth: { bottom: STACKED_BAR_SEPARATOR_WIDTH },
        borderSkipped: false,
        stack: STACK_KEY,
        borderRadius: {
          topLeft: i === topPositiveIdx ? roundedCorner : 0,
          topRight: i === topPositiveIdx ? roundedCorner : 0,
          bottomLeft: i === bottomNegativeIdx ? roundedCorner : 0,
          bottomRight: i === bottomNegativeIdx ? roundedCorner : 0,
        },
      })),
    };
  })();

  const chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 200 },
    plugins: {
      legend: { display: false },
      tooltip: {
        animation: { duration: 150 },
        backgroundColor: '#fff',
        bodyColor: '#212529',
        titleColor: '#212529',
        borderColor: 'rgba(0, 0, 0, 0.16)',
        borderWidth: 1,
        cornerRadius: 8,
        caretSize: 6,
        position: 'segmentCenter' as const,
        xAlign: 'center' as const,
        yAlign: 'bottom' as const,
        usePointStyle: true,
        boxWidth: SWATCH_SIZE,
        boxHeight: SWATCH_SIZE,
        padding: { top: 12, bottom: 12, left: 16, right: 16 },
        boxPadding: 4,
        callbacks: {
          title: () => '',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label(context: any) {
            const value = context.parsed?.y ?? 0;
            if (assets && context.dataset.label === contributionLabel) {
              return formatBreakdown(contributionBreakdown(assets), intl, formatCurrency);
            }
            if (assets && context.dataset.label === taxAdvantageLabel) {
              return formatBreakdown(taxWinBreakdown(assets), intl, formatCurrency);
            }
            return `${context.dataset.label}: ${formatCurrency(value)}`;
          },
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        color: '#212529',
        clamp: true,
        font: {
          family: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          size: 16,
          lineHeight: 1.25,
          weight: 'bold' as const,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter(_value: number, context: any) {
          const { datasets } = context.chart.data;
          let topPositiveIndex = -1;
          datasets.forEach((ds: { data: number[] }, i: number) => {
            if ((ds.data[context.dataIndex] ?? 0) > 0) {
              topPositiveIndex = i;
            }
          });
          if (context.datasetIndex !== topPositiveIndex) {
            return '';
          }
          const total = datasets.reduce(
            (acc: number, ds: { data: number[] }) => acc + (ds.data[context.dataIndex] ?? 0),
            0,
          );
          return formatCurrency(total);
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
      },
      y: {
        stacked: true,
        // Reserve headroom above the stack so the total datalabel isn't clipped.
        max: segments ? positiveStackTotal * 1.2 : undefined,
        ticks: { display: false },
        border: { display: false },
        grid: {
          display: true,
          drawTicks: false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          color: (ctx: any) => (ctx.tick?.value === 0 ? 'rgba(0, 0, 0, 0.175)' : 'rgba(0,0,0,0)'),
          lineWidth: 1,
        },
      },
    },
  };

  const renderAccountRow = (labelId: TranslationKey, amount: number, swatchColor: string) => {
    const rowId = labelId.replace(/\./g, '-');
    return (
      <div
        key={labelId}
        className="d-flex justify-content-between align-items-center gap-2 py-2 border-bottom"
      >
        <dt
          id={rowId}
          className="d-flex align-items-center gap-2 m-0 fw-normal"
          style={{ minWidth: 0 }}
        >
          <span
            className="flex-shrink-0"
            aria-hidden
            style={{
              display: 'inline-block',
              width: SWATCH_SIZE,
              height: SWATCH_SIZE,
              borderRadius: '50%',
              backgroundColor: swatchColor,
            }}
          />
          <span className="text-truncate">
            <FormattedMessage id={labelId} />
          </span>
        </dt>
        <dd aria-labelledby={rowId} className="m-0">
          {formatCurrency(amount)}
        </dd>
      </div>
    );
  };

  return (
    <div
      className="col-12 col-md-10 col-lg-9 mx-auto"
      style={{ textWrap: 'pretty' } as React.CSSProperties}
    >
      <div className="d-flex flex-column gap-5">
        <div className="d-flex flex-column gap-3">
          <h1 className="m-0">
            {!assets ? <Shimmer height={34} /> : <FormattedMessage id="secondPillarGrowth.title" />}
          </h1>
          {!assets ? (
            <Shimmer height={60} />
          ) : (
            <p className="m-0 lead">
              <FormattedMessage
                id="secondPillarGrowth.lead"
                values={{
                  balance: formatCurrency(assets.balance),
                  ownPercentage:
                    segments && positiveStackTotal > 0
                      ? Math.round((segments.contribution / positiveStackTotal) * 100)
                      : 0,
                  b: (chunks: string) => <strong>{chunks}</strong>,
                }}
              />
            </p>
          )}
        </div>

        <div>
          <div className="card px-3 py-3 px-sm-4 py-sm-4">
            <div className="row g-4 align-items-center">
              <div className="col-md-7">
                <div style={{ minHeight: '360px' }}>
                  {!chartData ? (
                    <Shimmer height={340} />
                  ) : (
                    <Chart type="bar" data={chartData} options={chartOptions} />
                  )}
                </div>
              </div>

              <div className="col-md-5">
                {segments && assets ? (
                  <dl className="m-0" data-testid="account-list">
                    {orderedAccountRows(segments).map((row) =>
                      renderAccountRow(row.labelId, row.amount, row.color),
                    )}
                    <div className="d-flex justify-content-between align-items-center gap-2 py-2 fw-bold">
                      <dt id="secondPillarGrowth-account-balance" className="m-0">
                        <FormattedMessage id="secondPillarGrowth.account.balance" />
                      </dt>
                      <dd aria-labelledby="secondPillarGrowth-account-balance" className="m-0">
                        {formatCurrency(assets.balance)}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <div className="d-flex flex-column gap-2" data-testid="account-list-shimmer">
                    <Shimmer height={30} />
                    <Shimmer height={30} />
                    <Shimmer height={30} />
                    <Shimmer height={30} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {assets && (
            <div className="text-secondary mt-2" data-testid="first-pillar-note">
              <FormattedMessage
                id="secondPillarGrowth.firstPillarNote"
                values={{
                  a: (chunks: string) => (
                    <Link to="/1st-vs-2nd-pillar" className="text-secondary">
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </div>
          )}
          {subNoteKey && (
            <div className="text-secondary mt-1" data-testid="sub-note">
              <FormattedMessage id={`secondPillarGrowth.subNote.${subNoteKey}`} />
            </div>
          )}
          {assets?.pikFlag && (
            <div className="text-secondary mt-1" role="note" data-testid="pik-disclaimer">
              <FormattedMessage id="secondPillarGrowth.pikDisclaimer" />
            </div>
          )}
        </div>

        <div className="d-flex flex-column gap-3">
          {!user || !conversion ? (
            <>
              <Shimmer height={28} />
              <Shimmer height={30} />
              <Shimmer height={48} />
            </>
          ) : (
            ctaContent
          )}
        </div>

        {thirdPillarUpsell && <div className="d-flex flex-column gap-3">{thirdPillarUpsell}</div>}

        <div className="d-flex flex-column gap-3" data-testid="methodology">
          <h2 className="m-0">
            <button
              id="methodologyToggle"
              className="btn p-0 border-0 focus-ring d-flex align-items-center gap-1 fw-normal"
              type="button"
              onClick={() => setMethodologyOpen(!methodologyOpen)}
              aria-expanded={methodologyOpen}
              aria-controls="methodologyContent"
            >
              <FormattedMessage id="secondPillarGrowth.methodology.summary" />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                fill="currentColor"
                viewBox="0 0 16 16"
                style={{
                  transform: methodologyOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <path d="m12.14 8.753-5.482 4.796c-.646.566-1.658.106-1.658-.753V3.204a1 1 0 0 1 1.659-.753l5.48 4.796a1 1 0 0 1 0 1.506z" />
              </svg>
            </button>
          </h2>
          <Collapse in={methodologyOpen}>
            <div id="methodologyContent" aria-labelledby="methodologyToggle">
              <div className="d-flex flex-column gap-3">
                <p className="m-0">
                  <FormattedMessage id="secondPillarGrowth.methodology.intro" />
                </p>
                <ul className="m-0">
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.own"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.state"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.return"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.inheritance"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.withdrawals"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id="secondPillarGrowth.methodology.item.pik"
                      values={{ b: (c: string) => <strong>{c}</strong> }}
                    />
                  </li>
                </ul>
                <p className="m-0">
                  <FormattedMessage id="secondPillarGrowth.methodology.source" />
                </p>
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default SecondPillarGrowth;
